import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

/**
 * Server-side drafts hook (R13). Drafts are stored in a separate
 * MongoDB collection — they NEVER affect inventory, reports, or the
 * live Sales Orders / Returns / POS lists.
 *
 * Usage:
 *   const { drafts, currentDraftId, saveDraft, deleteDraft, resumeDraft, clearCurrentDraft, refresh }
 *     = useServerDrafts('sales_order');
 *
 *   // Debounced save on every form change:
 *   useEffect(() => { saveDraft({ data: formState, label: 'Walk-in $42.50' }); },
 *            [formState, saveDraft]);
 */
export function useServerDrafts(type) {
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [loading, setLoading] = useState(false);
  const saveTimerRef = useRef(null);
  const inflightRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const list = await api.request(`/api/drafts?type=${encodeURIComponent(type)}`);
      setDrafts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.warn('Failed to load drafts:', err);
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Debounced save (1s). Pass `{ data, label }`. The hook tracks the current draft id. */
  const saveDraft = useCallback(({ data, label }) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (inflightRef.current) return;
      inflightRef.current = true;
      try {
        const body = { type, data, label: label || '' };
        if (currentDraftId) body.id = currentDraftId;
        const saved = await api.request('/api/drafts', { method: 'POST', body: JSON.stringify(body) });
        if (saved?.id && saved.id !== currentDraftId) setCurrentDraftId(saved.id);
        // Update the list optimistically
        setDrafts(prev => {
          const others = prev.filter(d => d.id !== saved.id);
          return [saved, ...others];
        });
      } catch (err) {
        console.warn('Failed to save draft:', err);
      } finally {
        inflightRef.current = false;
      }
    }, 1000);
  }, [type, currentDraftId]);

  const deleteDraft = useCallback(async (id) => {
    try {
      await api.request(`/api/drafts/${id}`, { method: 'DELETE' });
      setDrafts(prev => prev.filter(d => d.id !== id));
      if (id === currentDraftId) setCurrentDraftId(null);
    } catch (err) {
      console.warn('Failed to delete draft:', err);
    }
  }, [currentDraftId]);

  /** Set the current draft id without loading data — call before applying data via callback. */
  const resumeDraft = useCallback((id) => {
    setCurrentDraftId(id);
  }, []);

  /** Clear in-memory current-draft tracking (call after Confirm or Cancel). */
  const clearCurrentDraft = useCallback(() => {
    setCurrentDraftId(null);
  }, []);

  return { drafts, currentDraftId, loading, saveDraft, deleteDraft, resumeDraft, clearCurrentDraft, refresh, setCurrentDraftId };
}
